import React, { useState } from "react";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import PasswordStrength from "@/components/PasswordStrength";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { current: "", next: "", confirm: "" };
const FIELDS = [
  ["current", "Current password", "change-pw-current"],
  ["next", "New password", "change-pw-new"],
  ["confirm", "Confirm new password", "change-pw-confirm"],
];

export default function ChangePasswordCard() {
  const [form, setForm] = useState(EMPTY);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current) return toast.error("Enter your current password");
    if (form.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (form.next !== form.confirm) return toast.error("New passwords don't match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vault-card p-6" data-testid="change-password-card">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-[Outfit] text-xl font-medium flex items-center gap-2"><KeyRound size={18} /> Change password</h2>
        <button type="button" onClick={() => setShow((s) => !s)} className="text-sm inline-flex items-center gap-1 text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)]" data-testid="change-pw-toggle-visibility">
          {show ? <EyeOff size={15} /> : <Eye size={15} />} {show ? "Hide" : "Show"}
        </button>
      </div>
      <form onSubmit={submit} className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {FIELDS.map(([k, label, tid]) => (
          <div key={k}>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">{label}</Label>
            <Input
              type={show ? "text" : "password"}
              value={form[k]}
              onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              autoComplete={k === "current" ? "current-password" : "new-password"}
              className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
              data-testid={tid}
            />
            {k === "next" && form.next && <div className="mt-2"><PasswordStrength value={form.next} /></div>}
          </div>
        ))}
        <div className="sm:col-span-3 flex justify-end">
          <Button type="submit" disabled={saving} className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid="change-pw-submit">
            {saving ? "Updating…" : "Update password"}
          </Button>
        </div>
      </form>
    </div>
  );
}
